/**
 * Local heuristic structuring engine (design §15).
 * Turns the free-text operative note + structured fields into a Ficha
 * (structured summary). Deterministic; no backend, no external calls.
 * Text output follows the language passed via `t`.
 */
import type { TFunction } from 'i18next';
import { format } from 'date-fns';
import { ROLE_LABEL_KEY, TECHNIQUE_LABEL_KEY } from '@/components/Chips';
import i18n, { activeDateLocale } from '@/i18n';
import { getProcedureDisplayName } from './catalog';
import {
  capitalizeStep,
  containsAny,
  extractSteps,
  MATERIALS_LEXICON,
  RISK_LEXICON,
  splitSentences,
} from './operative-text';

type FichaT = TFunction;
import type { Surgery, Ficha } from './types';

/** Detects sentences with operative verbs/markers -> key steps */
export const extractKeySteps = (text: string, max = 7): string[] => extractSteps(text, { max });

export { splitSentences };

/** Lowercase + accent fold, for lexicon comparisons */
function normalize(txt: string): string {
  return txt.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

const capitalize = capitalizeStep;

/** Extracts instrument mentions from the text and merges them with the tags */
export function extractMaterials(c: Surgery): string[] {
  const norm = normalize(`${c.descripcion} ${c.materiales.join(' ')}`);
  const found = new Set<string>();
  for (const item of c.materiales) {
    const clean = item.trim();
    if (clean) found.add(clean.charAt(0).toUpperCase() + clean.slice(1));
  }
  for (const term of MATERIALS_LEXICON) {
    if (norm.includes(normalize(term))) {
      const name = term.charAt(0).toUpperCase() + term.slice(1);
      // Avoids trivial duplicates against existing tags
      const already = [...found].some((e) => normalize(e).includes(normalize(term)));
      if (!already) found.add(name);
    }
  }
  return [...found];
}

/**
 * "sin complicaciones", "no bleeding": a denied risk is not a critical point,
 * and it is how most uneventful notes end.
 */
const NEGATED_RISK =
  /\b(?:sin|no|without|free\s+of|libre\s+de)\s+(?:\w+\s+){0,2}?(?:complicacion(?:es)?|complications?|incidencias|sangrado|bleeding|hemorragia|hemorrhage|lesion(?:es)?|injur\w*|adherencias?|adhesions?)\b/g;

/** Flags sentences matching the risk lexicon → critical points */
export function extractCriticalPoints(c: Surgery, max = 5): string[] {
  const sentences = splitSentences(c.descripcion);
  const points = sentences
    .filter((f) => containsAny(normalize(f).replace(NEGATED_RISK, ' '), RISK_LEXICON))
    .map(capitalize);
  if (c.complicaciones.hubo && c.complicaciones.descripcion) {
    const note = capitalize(c.complicaciones.descripcion.trim());
    if (!points.some((p) => normalize(p) === normalize(note))) points.push(note);
  }
  return points.slice(0, max);
}

function readableDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Local order per language: es "20 de julio de 2026" · en "July 20, 2026"
  return format(
    d,
    i18n.language === 'es' ? "d 'de' MMMM 'de' yyyy" : 'MMMM d, yyyy',
    { locale: activeDateLocale() },
  );
}

export function formatDuration(min: number, t: FichaT): string {
  if (min < 60) return `${min} ${t('ficha.duration.min')}`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  const hourLabel = h === 1 ? t('ficha.duration.hour') : t('ficha.duration.hours');
  return m === 0 ? `${h} ${hourLabel}` : `${h} ${hourLabel} ${m} ${t('ficha.duration.min')}`;
}

/** Synthesizes the header summary (2–3 lines) */
export function synthesizeSummary(c: Surgery, t: FichaT): string {
  const sentences = splitSentences(c.descripcion);
  const first = sentences.length > 0 ? capitalize(sentences[0]) : '';
  const patient = t('ficha.summary.patient', { age: c.paciente.edad, sex: c.paciente.sexo });
  const parts = [
    t('ficha.summary.line', {
      procedure: getProcedureDisplayName(c.categoria).replace(/^./, (ch) => ch.toUpperCase()),
      date: readableDate(c.fecha),
      technique: t(`common.techniques.${TECHNIQUE_LABEL_KEY[c.tecnica]}`).toLowerCase(),
      patient,
      role: t(`common.roles.${ROLE_LABEL_KEY[c.rol]}`).toLowerCase(),
    }),
  ];
  if (first && normalize(first).slice(0, 60) !== normalize(c.categoria).slice(0, 60)) {
    parts.push(first.endsWith('.') ? first : `${first}.`);
  }
  parts.push(t('ficha.summary.duration', { duration: formatDuration(c.duracionMin, t) }));
  return parts.join(' ');
}

function techniqueDetail(c: Surgery, materials: string[], t: FichaT): string {
  const approaches: Record<string, string> = {
    Open: t('ficha.technique.open'),
    Laparoscopic: t('ficha.technique.laparoscopic'),
    Robotic: t('ficha.technique.robotic'),
    Endoscopic: t('ficha.technique.endoscopic'),
    Other: t('ficha.technique.other'),
  };
  const mat = materials.length > 0
    ? ` ${t('ficha.technique.materials', { materials: materials.join(', ') })}`
    : '';
  return `${t('ficha.technique.line', {
    approach: approaches[c.tecnica],
    role: t(`common.roles.${ROLE_LABEL_KEY[c.rol]}`).toLowerCase(),
  })}${mat}`;
}

function complicationsNote(c: Surgery, t: FichaT): string {
  if (!c.complicaciones.hubo) {
    return t('ficha.complications.none');
  }
  const grade = c.complicaciones.grado
    ? ` ${t('ficha.complications.grade', { grade: c.complicaciones.grado })}`
    : '';
  const desc = c.complicaciones.descripcion?.trim();
  return desc ? `${capitalize(desc)}${grade}.` : `${t('ficha.complications.unnamed')}${grade}.`;
}

function followUpNote(c: Surgery, t: FichaT): string | undefined {
  const s = c.seguimiento;
  if (!s) return undefined;
  const parts: string[] = [];
  if (s.diasHospitalizacion != null) {
    parts.push(t('ficha.followUp.hospitalStay', {
      count: s.diasHospitalizacion,
      days: s.diasHospitalizacion,
    }));
  }
  const longTerm = s.complicacionesLargoPlazo ?? [];
  if (longTerm.length > 0) {
    const list = longTerm.join(', ');
    parts.push(t('ficha.followUp.longTermComplications', { list }));
  }
  if (s.anatomiaPatologica?.trim()) {
    parts.push(t('ficha.followUp.pathology', { report: s.anatomiaPatologica.trim() }));
  }
  if (s.notasSeguimiento?.trim()) {
    parts.push(t('ficha.followUp.followUpNotes', { notes: s.notasSeguimiento.trim() }));
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

function findingsNote(c: Surgery, t: FichaT): string | undefined {
  const h = c.hallazgos?.trim();
  if (!h) return undefined;
  return t('ficha.findings.note', { findings: h });
}

function synthesizeLessons(c: Surgery, criticalPoints: string[], t: FichaT): string[] {
  const lessons: string[] = [];
  const own = splitSentences(c.lecciones || '');
  for (const f of own) lessons.push(capitalize(f));
  if (lessons.length === 0 && criticalPoints.length > 0) {
    const point = `${criticalPoints[0].charAt(0).toLowerCase()}${criticalPoints[0].slice(1)}`;
    lessons.push(t('ficha.lessons.attentionTo', { point }));
  }
  if (lessons.length === 0) {
    lessons.push(t('ficha.lessons.planning', { procedure: c.categoria.toLowerCase() }));
  }
  return lessons.slice(0, 4);
}

/**
 * Generates the structured summary (Ficha) of a procedure.
 * Sequence: split → key steps → materials → critical points → synthesis.
 */
export function generateFicha(c: Surgery, t: FichaT): Ficha {
  const materials = extractMaterials(c);
  const criticalPoints = extractCriticalPoints(c);
  return {
    resumen: synthesizeSummary(c, t),
    // Steps written (or dictated) by the surgeon win over the ones inferred
    // from the operative note.
    pasosClave: c.pasos?.length ? [...c.pasos] : extractKeySteps(c.descripcion),
    tecnicaDetalle: techniqueDetail(c, materials, t),
    puntosCriticos: criticalPoints,
    complicacionesNota: complicationsNote(c, t),
    lecciones: synthesizeLessons(c, criticalPoints, t),
    seguimientoNota: followUpNote(c, t),
    hallazgosNota: findingsNote(c, t),
    lang: i18n.language?.startsWith('es') ? 'es' : 'en',
    generadaEn: new Date().toISOString(),
  };
}
